import { useState } from 'react';
import { X, Save } from 'lucide-react';
import { RoboticsSections, SectionText } from '../hooks/useRoboticsSections';

interface RoboticsSectionsEditorProps {
  sections: RoboticsSections;
  onSave: (sections: RoboticsSections) => Promise<void>;
  onClose: () => void;
}

export default function RoboticsSectionsEditor({ sections, onSave, onClose }: RoboticsSectionsEditorProps) {
  const [editedSections, setEditedSections] = useState<RoboticsSections>(sections);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(editedSections);
      onClose();
    } catch (error) {
      console.error('Error saving sections:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (
    sectionKey: keyof RoboticsSections,
    field: keyof SectionText,
    lang: 'cs' | 'en' | 'de' | 'sk',
    value: string
  ) => {
    setEditedSections(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: {
          ...prev[sectionKey][field],
          [lang]: value
        }
      }
    }));
  };

  const renderSectionEditor = (sectionKey: keyof RoboticsSections, label: string) => {
    const section = editedSections[sectionKey];

    return (
      <div className="mb-8 bg-gray-800/50 rounded-xl p-6 border border-blue-500/20">
        <h3 className="text-xl font-bold text-blue-400 mb-4 font-orbitron">{label}</h3>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Nadpis / Title</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(['cs', 'en', 'de', 'sk'] as const).map((lang) => (
                <div key={lang}>
                  <label className="block text-xs text-gray-400 mb-1">{lang.toUpperCase()}</label>
                  <input
                    type="text"
                    value={section.title[lang]}
                    onChange={(e) => updateSection(sectionKey, 'title', lang, e.target.value)}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Popis / Description</label>
            <div className="grid grid-cols-1 gap-3">
              {(['cs', 'en', 'de', 'sk'] as const).map((lang) => (
                <div key={lang}>
                  <label className="block text-xs text-gray-400 mb-1">{lang.toUpperCase()}</label>
                  <textarea
                    value={section.description[lang]}
                    onChange={(e) => updateSection(sectionKey, 'description', lang, e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-blue-500/30 rounded-2xl w-full max-w-4xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-2xl font-bold text-white font-orbitron">Upravit texty sekcí</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {renderSectionEditor('products', 'Produkty')}
          {renderSectionEditor('services', 'Služby')}
          {renderSectionEditor('references', 'Reference')}
        </div>

        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-800">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            disabled={saving}
          >
            Zrušit
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Ukládám...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Uložit změny</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
